const STORAGE_KEY = 'ctOrderLatency.v1';
const MAX_OPERATIONS = 200;
const MAX_STREAM_EVENTS = 200;
const MATCH_WINDOW_MS = 10 * 60 * 1000;

function emptyState() {
  return { version: 1, operations: [], streamEvents: [] };
}

function loadState() {
  if (typeof window === 'undefined' || !window.localStorage) return emptyState();
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (parsed?.version !== 1) return emptyState();
    return {
      version: 1,
      operations: Array.isArray(parsed.operations) ? parsed.operations : [],
      streamEvents: Array.isArray(parsed.streamEvents) ? parsed.streamEvents : [],
    };
  } catch (_) {
    return emptyState();
  }
}

function saveState(state) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: 1,
      operations: state.operations.slice(-MAX_OPERATIONS),
      streamEvents: state.streamEvents.slice(-MAX_STREAM_EVENTS),
    }));
  } catch (_) {
    // Temporary diagnostics must never interfere with trading.
  }
}

function operationId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function orderIdsFromResponse(result) {
  const candidates = [
    result?.OrderID,
    result?.order_id,
    ...(Array.isArray(result?.Orders) ? result.Orders.map((order) => order?.OrderID || order?.order_id) : []),
  ];
  return [...new Set(candidates.filter((value) => value != null).map(String))];
}

function symbolFromOrder(order) {
  return order?.Symbol || order?.symbol || order?.Legs?.[0]?.Symbol || null;
}

function pricesMatch(operation, order) {
  if (operation.type !== 'replace') return true;
  const expected = operation.expected || {};
  const pairs = [
    [expected.LimitPrice, order?.LimitPrice],
    [expected.StopPrice, order?.StopPrice],
  ].filter(([value]) => value != null);
  if (pairs.length === 0) return true;
  return pairs.every(([expectedValue, actualValue]) => {
    const expectedNumber = Number(expectedValue);
    const actualNumber = Number(actualValue);
    if (Number.isFinite(expectedNumber) && Number.isFinite(actualNumber)) {
      return Math.abs(expectedNumber - actualNumber) < 0.000001;
    }
    return String(expectedValue) === String(actualValue);
  });
}

function isCancelledStatus(status) {
  const normalized = String(status || '').trim().toUpperCase();
  return ['CAN', 'CXL', 'CANCELLED', 'CANCELED', 'OUT', 'OUT CAN'].includes(normalized);
}

function streamMatches(operation, event) {
  if (!event.orderId || !operation.orderIds?.includes(event.orderId)) return false;
  if (event.receivedAt < operation.submittedAt) return false;
  if (event.receivedAt - operation.submittedAt > MATCH_WINDOW_MS) return false;
  if (operation.type === 'cancel') return isCancelledStatus(event.status);
  if (operation.type === 'replace') return pricesMatch(operation, event.order);
  return true;
}

function completedMetrics(operation) {
  return {
    apiMs: operation.apiRespondedAt != null
      ? operation.apiRespondedAt - operation.submittedAt
      : null,
    streamFromSubmitMs: operation.streamReceivedAt != null
      ? operation.streamReceivedAt - operation.submittedAt
      : null,
    streamAfterApiMs:
      operation.streamReceivedAt != null && operation.apiRespondedAt != null
        ? operation.streamReceivedAt - operation.apiRespondedAt
        : null,
  };
}

function average(values) {
  const numbers = values.filter(Number.isFinite);
  if (numbers.length === 0) return null;
  return Math.round(numbers.reduce((sum, value) => sum + value, 0) / numbers.length);
}

export function getOrderLatencySamples() {
  return loadState().operations.map((operation) => ({
    ...operation,
    ...completedMetrics(operation),
  }));
}

export function printOrderLatencySummary() {
  const samples = getOrderLatencySamples();
  const summary = ['create', 'cancel', 'replace'].map((type) => {
    const rows = samples.filter((sample) => sample.type === type && sample.success !== false);
    const completed = rows.filter((sample) => sample.streamReceivedAt != null);
    return {
      operation: type,
      attempts: rows.length,
      streamSamples: completed.length,
      avgApiMs: average(rows.map((sample) => sample.apiMs)),
      avgSubmitToStreamMs: average(completed.map((sample) => sample.streamFromSubmitMs)),
      avgApiToStreamMs: average(completed.map((sample) => sample.streamAfterApiMs)),
    };
  });
  console.groupCollapsed('[CT latency] averages');
  console.table(summary);
  console.log('Samples:', samples);
  console.groupEnd();
  return summary;
}

function finishFromKnownStreamEvents(state, operation) {
  if (operation.streamReceivedAt != null) return;
  const match = state.streamEvents.find((event) => streamMatches(operation, event));
  if (!match) return;
  operation.streamReceivedAt = match.receivedAt;
  operation.streamOrder = match.order;
}

export function startOrderLatency(type, details = {}) {
  const state = loadState();
  const now = Date.now();
  const operation = {
    id: operationId(),
    type,
    submittedAt: now,
    submittedAtIso: new Date(now).toISOString(),
    orderIds: details.orderId != null ? [String(details.orderId)] : [],
    accountId: details.accountId || null,
    symbol: details.symbol || null,
    expected: details.expected || null,
  };
  state.operations.push(operation);
  saveState(state);
  console.log(`[CT latency] ${type} submitted`, operation);
  return operation.id;
}

export function recordOrderApiResponse(id, result) {
  const state = loadState();
  const operation = state.operations.find((item) => item.id === id);
  if (!operation) return;
  operation.apiRespondedAt = Date.now();
  operation.apiRespondedAtIso = new Date(operation.apiRespondedAt).toISOString();
  operation.success = !(result?.error || result?.Error);
  operation.orderIds = [...new Set([
    ...(operation.orderIds || []),
    ...orderIdsFromResponse(result),
  ])];
  finishFromKnownStreamEvents(state, operation);
  saveState(state);
  console.log(`[CT latency] ${operation.type} API response`, {
    ...operation,
    ...completedMetrics(operation),
  });
  printOrderLatencySummary();
}

export function recordOrderApiError(id, error) {
  const state = loadState();
  const operation = state.operations.find((item) => item.id === id);
  if (!operation) return;
  operation.apiRespondedAt = Date.now();
  operation.apiRespondedAtIso = new Date(operation.apiRespondedAt).toISOString();
  operation.success = false;
  operation.error = error?.message || String(error || 'Unknown error');
  saveState(state);
  console.log(`[CT latency] ${operation.type} API error`, operation);
  printOrderLatencySummary();
}

export function recordOrderStreamUpdate(order) {
  const orderId = order?.OrderID || order?.order_id;
  if (orderId == null) return;
  const state = loadState();
  const receivedAt = Date.now();
  const event = {
    orderId: String(orderId),
    receivedAt,
    receivedAtIso: new Date(receivedAt).toISOString(),
    status: order?.Status || order?.normalized_status || null,
    order: {
      OrderID: String(orderId),
      Symbol: symbolFromOrder(order),
      Status: order?.Status || order?.normalized_status || null,
      LimitPrice: order?.LimitPrice ?? order?.order_limit_price ?? null,
      StopPrice: order?.StopPrice ?? order?.stop_price ?? null,
    },
  };
  state.streamEvents.push(event);

  const completed = [];
  for (const operation of state.operations) {
    if (operation.streamReceivedAt != null || operation.success === false) continue;
    if (!streamMatches(operation, event)) continue;
    operation.streamReceivedAt = receivedAt;
    operation.streamReceivedAtIso = event.receivedAtIso;
    operation.streamOrder = event.order;
    completed.push(operation);
  }
  saveState(state);

  for (const operation of completed) {
    console.log(`[CT latency] ${operation.type} stream update`, {
      ...operation,
      ...completedMetrics(operation),
    });
  }
  if (completed.length > 0) printOrderLatencySummary();
}

export function clearOrderLatencySamples() {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem(STORAGE_KEY);
  }
  console.log('[CT latency] samples cleared');
}

if (typeof window !== 'undefined') {
  window.ctOrderLatency = {
    print: printOrderLatencySummary,
    samples: getOrderLatencySamples,
    clear: clearOrderLatencySamples,
  };
}
